import type { Signal, WritableSignal } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { RouterModule } from '@angular/router';

import type { AppIdentity, Organization, UserConfig } from '@evc/platform';
import { OrganizationsService, PlatformComponent, PlatformService } from '@evc/platform';
import type { Maybe, Menu, MenuItemType } from '@evc/web-components';
import { SharedConfigService } from '@shared/services/config/config.service';
import { I18nService } from '@shared/services/i18n/i18n.service';

import { CurrentOrgCardComponent } from './components/current-org-card/current-org-card.component';
import { NavConfigService } from './services/nav-config/nav-config.service';

@Component({
  imports: [RouterModule, PlatformComponent, CurrentOrgCardComponent],
  selector: 'evc-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent {
  leftbarConfig: WritableSignal<Menu[]>;
  helpMenu: WritableSignal<MenuItemType[]>;
  appIdentification: WritableSignal<AppIdentity>;
  i18nService = inject(I18nService);
  currentOrganization: Signal<Maybe<Organization>> = computed(() =>
    this.#organizationService.current(),
  );

  #destroyRef = inject(DestroyRef);
  readonly #navConfigService = inject(NavConfigService);
  readonly #config = inject(SharedConfigService);
  readonly #organizationService = inject(OrganizationsService);
  readonly #platformService = inject(PlatformService);

  #mockUser = signal<Maybe<UserConfig>>(undefined);
  mockUser = this.#mockUser.asReadonly();
  ready: Signal<Maybe<true>> = this.#platformService.ready;

  #autoFetchOrgInfos = effect(() => {
    if (!this.ready()) return;
    untracked(() => this.#organizationService.getOrganizationInfos());
  });

  constructor() {
    this.leftbarConfig = this.#navConfigService.leftbarConfig;
    this.appIdentification = this.#navConfigService.appIdentity;
    this.helpMenu = this.#navConfigService.helpMenu;

    this.#destroyRef.onDestroy(() => {
      this.#autoFetchOrgInfos.destroy();
    });
  }
}
